'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TipCalculator() {
  const t = useTranslations("tools.tipCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [billAmount, setBillAmount] = useState<string>('');
  const [tipPercent, setTipPercent] = useState<number>(15);
  const [numPeople, setNumPeople] = useState<number>(1);
  const [selectedTip, setSelectedTip] = useState<string>('15');
  const [results, setResults] = useState<{
    billAmount: number;
    tipAmount: number;
    totalAmount: number;
    tipPerPerson: number;
    totalPerPerson: number;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      {}
    ),
    toolTitle: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, margin: '0 0 0.5rem 0' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    toolSubtitle: getResponsiveStyle(
      { color: colors.textSecondary, margin: 0 },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    calculatorGrid: getResponsiveStyle(
      { display: 'grid', gap: '2rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    cardTitle: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, margin: '0 0 1.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: getResponsiveStyle(
      { display: 'block', fontWeight: 500, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    inputWrapper: {
      position: 'relative' as const,
    },
    inputIcon: {
      position: 'absolute' as const,
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 600,
    },
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' }
    ),
    inputWithIcon: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
      },
      { paddingLeft: '2.5rem', padding: '0.75rem 1rem 0.75rem 2.5rem' },
      { paddingLeft: '2.5rem', padding: '0.75rem 1rem 0.75rem 2.5rem' },
      { paddingLeft: '2.5rem', padding: '0.75rem 1rem 0.75rem 2.5rem' }
    ),
    tipOptions: getResponsiveStyle(
      { display: 'grid', gap: '0.75rem', marginBottom: '1rem' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(3, 1fr)' },
      { gridTemplateColumns: 'repeat(3, 1fr)' }
    ),
    tipBtn: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e2e8f0',
        background: 'white',
        borderRadius: '0.5rem',
        fontWeight: 600,
        color: '#334155',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
      },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    tipBtnActive: {
      borderColor: '#2563eb',
      background: '#2563eb',
      color: 'white',
    },
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem',
      },
      { padding: '0.875rem 1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1.1rem' },
      { padding: '1rem', fontSize: '1.1rem' }
    ),
    resultsCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, margin: '0 0 1.5rem 0', textAlign: 'center' as const },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    resultGrid: {
      display: 'grid',
      gap: '1rem',
    },
    resultItem: getResponsiveStyle(
      {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '0.5rem',
        borderLeft: '4px solid #2563eb',
      },
      { padding: '0.875rem' },
      { padding: '1rem' },
      { padding: '1rem' }
    ),
    resultItemHighlight: {
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      borderLeftColor: '#7c3aed',
    },
    resultLabel: getResponsiveStyle(
      { fontWeight: 500, color: '#475569' },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    resultValue: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultValueHighlight: {
      color: '#7c3aed',
    },
    perPersonSection: {
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: '2px dashed #e2e8f0',
    },
    perPersonTitle: getResponsiveStyle(
      { fontWeight: 600, color: '#64748b', marginBottom: '1rem', textAlign: 'center' as const },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    infoBox: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderLeft: '4px solid #f59e0b',
        borderRadius: '0.5rem',
        marginTop: '1.5rem',
      },
      { padding: '0.875rem' },
      { padding: '1rem' },
      { padding: '1rem' }
    ),
    infoBoxText: getResponsiveStyle(
      { margin: 0, color: '#78350f', lineHeight: 1.5 },
      { fontSize: '0.875rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', margin: '1rem 0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, margin: 0 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    refLink: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.3s ease',
    },
  };

  const handleTipSelect = (tip: string) => {
    setSelectedTip(tip);
    if (tip !== 'custom') {
      setTipPercent(parseFloat(tip));
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const bill = parseFloat(billAmount);

    if (isNaN(bill) || bill <= 0) {
      alert(t('errorInvalidBill'));
      return;
    }

    if (isNaN(tipPercent) || tipPercent < 0) {
      alert(t('errorInvalidTip'));
      return;
    }

    if (isNaN(numPeople) || numPeople < 1) {
      alert(t('errorInvalidPeople'));
      return;
    }

    const tipAmount = bill * (tipPercent / 100);
    const totalAmount = bill + tipAmount;
    const tipPerPerson = tipAmount / numPeople;
    const totalPerPerson = totalAmount / numPeople;

    setResults({
      billAmount: bill,
      tipAmount,
      totalAmount,
      tipPerPerson,
      totalPerPerson,
    });
  };

  const getQuickTip = (percent: number): string => {
    const bill = parseFloat(billAmount) || 0;
    return '$' + (bill * (percent / 100)).toFixed(2);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.toolTitle}>💰 {t('toolTitle')}</h2>
          <p style={styles.toolSubtitle}>{t('toolSubtitle')}</p>
        </div>

        <div style={styles.calculatorGrid}>
          <div style={styles.calculatorCard}>
            <h3 style={styles.cardTitle}>💵 {t('billDetailsTitle')}</h3>
            <form onSubmit={handleCalculate}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="bill-amount">{t('billAmountLabel')}</label>
                <div style={styles.inputWrapper}>
                  <span style={styles.inputIcon}>$</span>
                  <input
                    type="number"
                    id="bill-amount"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={billAmount}
                    onChange={(e) => setBillAmount(e.target.value)}
                    style={styles.inputWithIcon}
                    required
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('tipPercentageLabel')}</label>
                <div style={styles.tipOptions}>
                  {['10', '15', '18', '20', '25', 'custom'].map((tip) => (
                    <button
                      key={tip}
                      type="button"
                      onClick={() => handleTipSelect(tip)}
                      style={{
                        ...styles.tipBtn,
                        ...(selectedTip === tip ? styles.tipBtnActive : {}),
                      }}
                    >
                      {tip === 'custom' ? t('customTip') : `${tip}%`}
                    </button>
                  ))}
                </div>
                <div style={styles.inputWrapper}>
                  <span style={styles.inputIcon}>%</span>
                  <input
                    type="number"
                    id="tip-percent"
                    step="0.1"
                    min="0"
                    max="100"
                    value={tipPercent}
                    onChange={(e) => {
                      setTipPercent(parseFloat(e.target.value));
                      setSelectedTip('custom');
                    }}
                    style={styles.inputWithIcon}
                    required
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="num-people">{t('numPeopleLabel')}</label>
                <input
                  type="number"
                  id="num-people"
                  min="1"
                  value={numPeople}
                  onChange={(e) => setNumPeople(parseInt(e.target.value))}
                  style={styles.input}
                  required
                />
              </div>

              <button type="submit" style={styles.calculateBtn}>
                {t('calculateButton')}
              </button>
            </form>
          </div>

          <div style={styles.calculatorCard}>
            <h3 style={styles.cardTitle}>📊 {t('quickReferenceTitle')}</h3>
            <div style={styles.resultGrid}>
              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('quickTip10')}</span>
                <span style={styles.resultValue}>{getQuickTip(10)}</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('quickTip15')}</span>
                <span style={styles.resultValue}>{getQuickTip(15)}</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('quickTip18')}</span>
                <span style={styles.resultValue}>{getQuickTip(18)}</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('quickTip20')}</span>
                <span style={styles.resultValue}>{getQuickTip(20)}</span>
              </div>
            </div>
            <div style={styles.infoBox}>
              <p style={styles.infoBoxText}>
                <strong>💡 {t('tipLabel')}</strong> {t('tipStandard')}
              </p>
            </div>
          </div>
        </div>

        {results && (
          <div style={styles.resultsCard}>
            <h3 style={styles.resultsTitle}>✨ {t('resultsTitle')}</h3>
            <div style={styles.resultGrid}>
              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('resultBillAmount')}</span>
                <span style={styles.resultValue}>${results.billAmount.toFixed(2)}</span>
              </div>
              <div style={{ ...styles.resultItem, ...styles.resultItemHighlight }}>
                <span style={styles.resultLabel}>{t('resultTipAmount')}</span>
                <span style={{ ...styles.resultValue, ...styles.resultValueHighlight }}>
                  ${results.tipAmount.toFixed(2)}
                </span>
              </div>
              <div style={{ ...styles.resultItem, ...styles.resultItemHighlight }}>
                <span style={styles.resultLabel}>{t('resultTotalAmount')}</span>
                <span style={{ ...styles.resultValue, ...styles.resultValueHighlight }}>
                  ${results.totalAmount.toFixed(2)}
                </span>
              </div>
            </div>

            {numPeople > 1 && (
              <div style={styles.perPersonSection}>
                <div style={styles.perPersonTitle}>👥 {t('perPersonTitle')}</div>
                <div style={styles.resultGrid}>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t('resultTipPerPerson')}</span>
                    <span style={styles.resultValue}>${results.tipPerPerson.toFixed(2)}</span>
                  </div>
                  <div style={{ ...styles.resultItem, ...styles.resultItemHighlight }}>
                    <span style={styles.resultLabel}>{t('resultTotalPerPerson')}</span>
                    <span style={{ ...styles.resultValue, ...styles.resultValueHighlight }}>
                      ${results.totalPerPerson.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToStep1')}</li>
          <li style={styles.listItem}>{t('howToStep2')}</li>
          <li style={styles.listItem}>{t('howToStep3')}</li>
          <li style={styles.listItem}>{t('howToStep4')}</li>
          <li style={styles.listItem}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph2')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph3')}</p>
      </section>

      {/* Details */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <p style={styles.paragraph}>{t('detailsParagraph1')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t('detailsFeature1Title')}</strong> {t('detailsFeature1')}</li>
          <li style={styles.listItem}><strong>{t('detailsFeature2Title')}</strong> {t('detailsFeature2')}</li>
          <li style={styles.listItem}><strong>{t('detailsFeature3Title')}</strong> {t('detailsFeature3')}</li>
          <li style={styles.listItem}><strong>{t('detailsFeature4Title')}</strong> {t('detailsFeature4')}</li>
          <li style={styles.listItem}><strong>{t('detailsFeature5Title')}</strong> {t('detailsFeature5')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a href="https://tip-calculator.pro" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              {t('ref1')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://kitemetric.com/blogs/ai-tip-calculator-your-smart-dining-companion" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              {t('ref2')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.nerdwallet.com/finance/learn/tip-calculator" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              {t('ref3')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.octalsoftware.com/blog/tip-calculator-app-development" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              {t('ref4')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.size.ly/calculator/tip-calculator" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              {t('ref5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}